
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { LogOut, Settings, UserRound, Moon, Sun } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCurrentUserRole } from "@/hooks/rbac/useCurrentUserRole";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

export function UserMenu() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();
  
  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Call all hooks unconditionally
  const { data: session } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    },
  });

  const { data: userRole } = useCurrentUserRole();
  
  const handleThemeToggle = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    console.log(`Switching theme from ${theme} to ${newTheme}`);
    setTheme(newTheme);
  };
  
  // Don't render theme-dependent content until mounted
  if (!mounted) {
    return null;
  }

  const firstName = session?.user?.user_metadata?.firstName;
  const lastName = session?.user?.user_metadata?.lastName;
  const userAvatar = session?.user?.user_metadata?.avatar;
  const displayName = firstName || lastName ? `${firstName || ''} ${lastName || ''}`.trim() : null;

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      
      // Clear the session from the query cache
      queryClient.setQueryData(['session'], null);
      
      toast.success("Logged out successfully");
      
      // Use navigate for SPA navigation
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error logging out:", error);
      toast.error("Error logging out");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="group relative">
          <Button 
            variant="ghost" 
            className={`relative h-8 p-0 rounded-full transition-all duration-300 ease-out overflow-hidden ${
              !isMobile ? 'group-hover:pr-3 group-data-[state=open]:pr-3' : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage 
                  src={userAvatar || `https://api.dicebear.com/7.x/lorelei/svg?seed=default`} 
                  alt="User avatar" 
                />
                <AvatarFallback>
                  <UserRound className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div className={`flex flex-col items-start transition-all duration-300 ease-out whitespace-nowrap overflow-hidden ${
                !isMobile 
                  ? 'opacity-0 w-0 group-hover:opacity-100 group-hover:w-auto group-data-[state=open]:opacity-100 group-data-[state=open]:w-auto' 
                  : 'opacity-0 w-0'
              }`}>
                {displayName && (
                  <span className="text-sm font-medium text-foreground leading-none">
                    {displayName}
                  </span>
                )}
                {userRole && (
                  <span className="text-[11px] text-muted-foreground capitalize leading-none mt-0.5">
                    {userRole}
                  </span>
                )}
                {!displayName && !userRole && (
                  <span className="text-sm font-medium text-foreground leading-none">
                    Account
                  </span>
                )}
              </div>
            </div>
            <span className="sr-only">User menu</span>
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate("/preferences")}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Preferences</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
